import { useState, useEffect, useMemo } from "react";
import {
  Package,
  Users,
  Calendar as CalendarIcon,
  TrendingUp,
  Cloud,
  ChevronLeft,
  ChevronRight,
  CalendarDays,
  CalendarRange,
  Clock,
  MapPin,
  Plus,
  Pencil,
  Trash2,
  FileImage,
  FileText,
  StickyNote,
  CalendarCheck,
  CalendarClock,
  Truck,
  DollarSign,
  Bell,
  BellOff,
  Activity,
  Wrench,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { analyticsApi, eventsApi, modelsApi, staffApi } from "@/lib/unifiedApi";
import { subscribeLeaves } from "@/lib/attendance";
import { getActivityLogs, logActivity, ACTION_META } from "@/lib/activityLog";
import ExportDialog from "@/components/ExportDialog";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { sendNotification } from "@/lib/messaging";
import { useNotifications } from "@/hooks/useNotifications";
import { 
  format, 
  startOfMonth, 
  endOfMonth, 
  eachDayOfInterval, 
  isSameMonth, 
  isSameDay, 
  addMonths, 
  subMonths,
  startOfWeek,
  endOfWeek,
  isToday,
  parseISO,
  compareAsc,
} from "date-fns";

const EVENT_EXPORT_COLUMNS = [
  { key: 'event_name',           label: 'Event Name' },
  { key: 'model_name',           label: 'Model' },
  { key: 'staff',                label: 'Staff' },
  { key: 'location',             label: 'Location' },
  { key: 'date_start',           label: 'Start Date' },
  { key: 'date_end',             label: 'End Date' },
  { key: 'time',                 label: 'Start Time' },
  { key: 'time_end',             label: 'End Time' },
  { key: 'status',               label: 'Status' },
  { key: 'deposit_status',       label: 'Deposit Status' },
  { key: 'full_paid',            label: 'Full Paid', format: (v) => (v ? 'Yes' : 'No') },
  { key: 'price',                label: 'Price (฿)' },
  { key: 'transportation_price', label: 'Transportation (฿)' },
  { key: 'note',                 label: 'Note' },
  { key: 'event_display_link',   label: 'Display Link' },
  { key: 'event_display_status', label: 'Display Status' },
  { key: 'template_link',        label: 'Template Link' },
  { key: 'template_status',      label: 'Template Status' },
];

const Dashboard = () => {
  const { isAdmin, user } = useAuth();
  const { permissionStatus, requestPermission, sendTestNotification } = useNotifications(user?.id);
  const [stats, setStats] = useState({ total_events: 0, total_inventory: 0, total_talent: 0 });
  const [events, setEvents] = useState([]);
  const [leaveRequests, setLeaveRequests] = useState([]);
  const [activityLogs, setActivityLogs] = useState([]);
  const [models, setModels] = useState([]);
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);

  const [formData, setFormData] = useState({
    event_name: "",
    model_name: "",
    location: "",
    location_map_link: "",
    date_start: format(new Date(), "yyyy-MM-dd"),
    date_end: "",
    time: "09:00",
    time_end: "",
    setup_date: "",
    setup_time: "",
    price: 0,
    deposit_status: "pending",
    full_paid: false,
    transportation_price: 0,
    status: "scheduled",
    staff: "",
    setup_staff: "",
    event_staff: "",
    event_display_link: "",
    event_display_status: "Pending",
    template_link: "",
    template_status: "Pending",
    note: "",
  });

  const statuses = ["scheduled", "confirmed", "completed", "cancelled"];
  const depositStatuses = ["pending", "partial", "paid", "not_required"];
  const uploadStatuses = ["Pending", "Done"];

  // Export filters — operates on ALL events (not filtered by visible month)
  const [exportFilters, setExportFilters] = useState({
    dateFrom: '',
    dateTo: '',
    status: 'all',
    depositStatus: 'all',
  });
  const exportData = events
    .filter((event) => {
      const eventDate = event.date_start || event.date || '';
      if (exportFilters.dateFrom && eventDate < exportFilters.dateFrom) return false;
      if (exportFilters.dateTo   && eventDate > exportFilters.dateTo)   return false;
      if (exportFilters.status !== 'all' && event.status !== exportFilters.status) return false;
      if (exportFilters.depositStatus !== 'all' && event.deposit_status !== exportFilters.depositStatus) return false;
      return true;
    })
    .sort((a, b) => {
      const aDate = a.date_start || a.date || '';
      const bDate = b.date_start || b.date || '';
      return aDate.localeCompare(bDate);
    });

  useEffect(() => {
    const unsubStats = analyticsApi.subscribeStats((data) => { setStats(data); setLoading(false); });
    const unsubEvents = eventsApi.subscribe((data) => setEvents(data));
    const unsubModels = modelsApi.subscribe((data) => setModels(data));
    const unsubStaff = staffApi.subscribe((data) => setStaff(data));
    const unsubLeaves = subscribeLeaves(setLeaveRequests);
    getActivityLogs(30).then(setActivityLogs);
    return () => { unsubStats(); unsubEvents(); unsubModels(); unsubStaff(); unsubLeaves(); };
  }, []);

  // Calendar helpers
  const monthStart = startOfMonth(currentMonth);
  const monthEnd = endOfMonth(currentMonth);
  const calendarStart = startOfWeek(monthStart, { weekStartsOn: 0 });
  const calendarEnd = endOfWeek(monthEnd, { weekStartsOn: 0 });
  const calendarDays = eachDayOfInterval({ start: calendarStart, end: calendarEnd });

  const getEventsForDay = (day) => {
    return events.filter((event) => {
      if (!event.date && !event.date_start) return false;
      try {
        const startDate = parseISO(event.date_start || event.date);
        const endDate = event.date_end ? parseISO(event.date_end) : startDate;
        return isSameDay(day, startDate) || isSameDay(day, endDate) || (day >= startDate && day <= endDate);
      } catch { return false; }
    });
  };

  // Events whose set-up day falls on `day` (set-up can be a day before the event)
  const getSetupsForDay = (day) => {
    return events.filter((event) => {
      if (!event.setup_date) return false;
      try { return isSameDay(day, parseISO(event.setup_date)); } catch { return false; }
    });
  };

  const getModelColor = (modelName) => {
    const model = models.find(m => m.model_name === modelName);
    return model?.tag_color || "#BFFF00";
  };

  const LEAVE_STYLE = {
    'ลาพักร้อน': { bg: 'rgba(99,102,241,0.2)',  text: '#a5b4fc', dot: '#818cf8', tagBg: 'rgba(99,102,241,0.35)',  tagText: '#c4b5fd', label: 'Annual'   },
    'ลาป่วย':    { bg: 'rgba(245,158,11,0.2)',  text: '#fcd34d', dot: '#f59e0b', tagBg: 'rgba(245,158,11,0.35)', tagText: '#fde68a', label: 'Sick'     },
    'sick':      { bg: 'rgba(245,158,11,0.2)',  text: '#fcd34d', dot: '#f59e0b', tagBg: 'rgba(245,158,11,0.35)', tagText: '#fde68a', label: 'Sick'     },
    'ลากิจ':     { bg: 'rgba(16,185,129,0.2)',  text: '#6ee7b7', dot: '#10b981', tagBg: 'rgba(16,185,129,0.3)',  tagText: '#a7f3d0', label: 'Personal' },
    'หยุดชดเชย':  { bg: 'rgba(6,182,212,0.2)',   text: '#67e8f9', dot: '#06b6d4', tagBg: 'rgba(6,182,212,0.35)',  tagText: '#a5f3fc', label: 'Comp'     },
  };
  const LEAVE_PENDING_STYLE = { bg: 'rgba(100,116,139,0.18)', text: '#94a3b8', dot: '#64748b', tagBg: 'rgba(100,116,139,0.3)', tagText: '#cbd5e1', label: 'Pending' };

  const getLeavesForDay = (day) => {
    const dateStr = format(day, 'yyyy-MM-dd');
    return leaveRequests.filter(l => {
      const start = l.startDate || '';
      const end = l.endDate || l.startDate || '';
      return dateStr >= start && dateStr <= end;
    });
  };

  const currentMonthEvents = events.filter((event) => {
    const d = event.date_start || event.date;
    if (!d) return false;
    try { return isSameMonth(parseISO(d), currentMonth); } catch { return false; }
  });
  const totalRevenue = currentMonthEvents.reduce((sum, event) => sum + (parseFloat(event.price) || 0), 0);
  const totalLogisticCost = currentMonthEvents.reduce((sum, event) => sum + (parseFloat(event.transportation_price) || 0), 0);
  const totalProfits = totalRevenue - totalLogisticCost;
  const selectedDayEvents = selectedDate ? getEventsForDay(selectedDate) : [];

  // Event statistics calculations
  const eventStats = useMemo(() => {
    const now = new Date();
    const currentMonthStart = startOfMonth(now);
    const currentMonthEnd = endOfMonth(now);
    const currentYearStart = new Date(now.getFullYear(), 0, 1);
    const currentYearEnd = new Date(now.getFullYear(), 11, 31);

    let eventsThisMonth = 0;
    let eventsThisYear = 0;
    let upcomingEvents = 0;

    events.forEach((event) => {
      const eventDate = event.date_start || event.date;
      if (!eventDate) return;
      
      try {
        const date = parseISO(eventDate);
        
        // Events this month
        if (date >= currentMonthStart && date <= currentMonthEnd) {
          eventsThisMonth++;
        }
        
        // Events this year
        if (date >= currentYearStart && date <= currentYearEnd) {
          eventsThisYear++;
        }
        
        // Upcoming events (from today onwards)
        if (date >= now) {
          upcomingEvents++;
        }
      } catch (e) {
        // Skip invalid dates
      }
    });

    return { eventsThisMonth, eventsThisYear, upcomingEvents };
  }, [events]);

  // Event CRUD
  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingEvent) {
        await eventsApi.update(editingEvent.id, formData);
        toast.success("Event updated");
        logActivity(user?.id, user?.name, 'event_update', formData.event_name || formData.model_name || editingEvent.id);
      } else {
        await eventsApi.create(formData);
        toast.success("Event created");
        logActivity(user?.id, user?.name, 'event_create', formData.event_name || formData.model_name || '');
        // 🔔 Push notification — new event
        sendNotification({
          type: 'new_event',
          title: 'Event ใหม่',
          body: `${formData.event_name || formData.name || 'Untitled'} · ${formData.date_start || ''}`,
          targetUserIds: 'all',
          data: { type: 'new_event', url: '/' },
        });
      }
      resetForm();
    } catch (error) {
      toast.error("Failed to save event");
    }
  };

  const handleEdit = (event) => {
    setEditingEvent(event);
    setFormData({
      event_name: event.event_name || "",
      model_name: event.model_name || "",
      location: event.location || "",
      location_map_link: event.location_map_link || "",
      date_start: event.date_start || event.date || "",
      date_end: event.date_end || "",
      time: event.time || "09:00",
      time_end: event.time_end || "",
      setup_date: event.setup_date || "",
      setup_time: event.setup_time || "",
      price: event.price || 0,
      deposit_status: event.deposit_status || "pending",
      full_paid: event.full_paid || false,
      transportation_price: event.transportation_price || 0,
      status: event.status || "scheduled",
      staff: event.staff || "",
      setup_staff: event.setup_staff || "",
      event_staff: event.event_staff || "",
      event_display_link: event.event_display_link || "",
      event_display_status: event.event_display_status || "Pending",
      template_link: event.template_link || "",
      template_status: event.template_status || "Pending",
      note: event.note || "",
    });
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("ลบ Event นี้?")) return;
    try {
      const target = events.find(e => e.id === id);
      await eventsApi.delete(id);
      toast.success("Event deleted");
      logActivity(user?.id, user?.name, 'event_delete', target?.event_name || target?.model_name || id);
    } catch (error) {
      toast.error("Failed to delete");
    }
  };

  const resetForm = () => {
    setEditingEvent(null);
    setFormData({
      event_name: "",
      model_name: "",
      location: "",
      location_map_link: "",
      date_start: format(selectedDate || new Date(), "yyyy-MM-dd"),
      date_end: "",
      time: "09:00",
      time_end: "",
      setup_date: "",
      setup_time: "",
      price: 0,
      deposit_status: "pending",
      full_paid: false,
      transportation_price: 0,
      status: "scheduled",
      staff: "",
      setup_staff: "",
      event_staff: "",
      event_display_link: "",
      event_display_status: "Pending",
      template_link: "",
      template_status: "Pending",
      note: "",
    });
    setDialogOpen(false);
  };

  const openAddEvent = () => {
    resetForm();
    setFormData(prev => ({ ...prev, date_start: format(selectedDate || new Date(), "yyyy-MM-dd") }));
    setDialogOpen(true);
  };

  if (loading) {
    return (
      <div className="page-container" data-testid="dashboard-loading">
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-2 border-[#BFFF00] border-t-transparent rounded-full animate-spin" />
        </div>
      </div>
    );
  }

  return (
    <div className="page-container" data-testid="dashboard">
      {/* Header */}
      <div className="flex items-center justify-between gap-3 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 tracking-tight">Dashboard</h1>
          <p className="text-gray-500 text-sm">ภาพรวมการดำเนินงาน</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="hidden sm:flex items-center gap-2 px-3 py-1.5 rounded-full bg-emerald-50 border border-emerald-200">
            <span className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse"></span>
            <span className="text-xs text-emerald-700 font-medium">Live Sync</span>
          </div>
          <ExportDialog
            title="Export Events"
            filename="events"
            data={exportData}
            columns={EVENT_EXPORT_COLUMNS}
            recordLabel="events"
          >
            <div className="space-y-3">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label className="text-gray-700 text-sm">From Date</Label>
                  <Input
                    type="date"
                    value={exportFilters.dateFrom}
                    onChange={(e) => setExportFilters((f) => ({ ...f, dateFrom: e.target.value }))}
                    className="border-gray-200"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-gray-700 text-sm">To Date</Label>
                  <Input
                    type="date"
                    value={exportFilters.dateTo}
                    onChange={(e) => setExportFilters((f) => ({ ...f, dateTo: e.target.value }))}
                    className="border-gray-200"
                  />
                </div>
              </div>
              <div className="space-y-1.5">
                <Label className="text-gray-700 text-sm">Event Status</Label>
                <Select
                  value={exportFilters.status}
                  onValueChange={(v) => setExportFilters((f) => ({ ...f, status: v }))}
                >
                  <SelectTrigger className="border-gray-200"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white border-gray-200">
                    <SelectItem value="all">All statuses</SelectItem>
                    {statuses.map((s) => (
                      <SelectItem key={s} value={s}>{s}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-gray-700 text-sm">Deposit Status</Label>
                <Select
                  value={exportFilters.depositStatus}
                  onValueChange={(v) => setExportFilters((f) => ({ ...f, depositStatus: v }))}
                >
                  <SelectTrigger className="border-gray-200"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white border-gray-200">
                    <SelectItem value="all">All deposit statuses</SelectItem>
                    {depositStatuses.map((s) => (
                      <SelectItem key={s} value={s}>{s.replace('_', ' ')}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </ExportDialog>
          {/* Notification test button — always register + test */}
          <button
            title="Test push notification (register token + send)"
            onClick={sendTestNotification}
            className="flex items-center gap-1.5 px-3 h-9 rounded-full text-xs font-semibold transition-colors"
            style={{
              background: permissionStatus === 'granted' ? 'rgba(191,255,0,0.15)' : 'rgba(255,100,100,0.15)',
              border: `1px solid ${permissionStatus === 'granted' ? 'rgba(191,255,0,0.4)' : 'rgba(255,100,100,0.3)'}`,
              color: permissionStatus === 'granted' ? '#BFFF00' : '#ff6464',
            }}
          >
            {permissionStatus === 'granted' ? <Bell size={14}/> : <BellOff size={14}/>}
            <span className="hidden sm:inline">Test Notif</span>
          </button>
          <Button
            data-testid="dashboard-add-event-btn"
            className="bg-[#BFFF00] hover:bg-[#a6e600] text-black font-medium rounded-full"
            onClick={openAddEvent}
          >
            <Plus size={18} className="sm:mr-2" />
            <span className="hidden sm:inline">Add Event</span>
          </Button>
        </div>
      </div>

      {/* Top Section: Overview + Selected Date */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        {/* Overview Card */}
        <Card className="bg-white border-gray-100 shadow-sm rounded-2xl">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold text-gray-900">Overview</CardTitle>
          </CardHeader>
          <CardContent className="p-4 pt-0">
            <div className="space-y-2">
              {[
                { label: "Total Events", value: stats.total_events, icon: CalendarIcon },
                { label: "Inventory", value: stats.total_inventory, icon: Package },
                { label: "Team", value: stats.total_talent, icon: Users },
              ].map((item) => (
                <div key={item.label} className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <item.icon size={14} className="text-gray-400" />
                    <span className="text-xs text-gray-600">{item.label}</span>
                  </div>
                  <span className="text-sm font-semibold text-gray-900">{item.value}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Selected Date Events */}
        <Card className="md:col-span-2 bg-white border-gray-100 shadow-sm rounded-2xl">
          <CardHeader className="pb-2 border-b border-gray-100">
            <CardTitle className="text-sm font-semibold text-gray-900 flex items-center gap-2">
              <CalendarIcon size={14} className="text-[#BFFF00]" />
              {selectedDate ? format(selectedDate, "d MMM yyyy") : "เลือกวันที่"}
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0 max-h-[150px] overflow-y-auto">
            {selectedDayEvents.length === 0 ? (
              <div className="p-4 text-center text-gray-400 text-sm">ไม่มี Event ในวันนี้</div>
            ) : (
              <div className="divide-y divide-gray-100">
                {selectedDayEvents.map((event) => (
                  <div key={event.id} className="p-3 hover:bg-gray-50 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-8 rounded-full" style={{ backgroundColor: getModelColor(event.model_name) }} />
                      <div>
                        <p className="text-sm font-medium text-gray-900">{event.event_name}</p>
                        <p className="text-xs text-gray-500">{event.model_name}</p>
                      </div>
                    </div>
                    <div className="flex gap-1">
                      <button onClick={() => handleEdit(event)} className="p-1.5 rounded hover:bg-gray-100 text-gray-400"><Pencil size={14} /></button>
                      <button onClick={() => handleDelete(event.id)} className="p-1.5 rounded hover:bg-red-50 text-gray-400 hover:text-red-500"><Trash2 size={14} /></button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Admin Only - Financial Cards Row */}
      {isAdmin() && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          {/* Revenue Card */}
          <Card className="border-0 shadow-sm rounded-2xl" style={{ backgroundColor: '#1a1a1a' }}>
            <CardContent className="p-4">
              <p className="text-xs text-gray-400 uppercase">Total Revenue</p>
              <p className="text-2xl font-bold mt-1 text-[#BFFF00]">฿{totalRevenue.toLocaleString()}</p>
              <div className="flex items-center gap-1 mt-2">
                <TrendingUp size={14} className="text-gray-400" />
                <span className="text-xs text-gray-400">{format(currentMonth, "MMMM yyyy")}</span>
              </div>
            </CardContent>
          </Card>

          {/* Logistic Cost Card */}
          <Card className="border-0 shadow-sm rounded-2xl" style={{ backgroundColor: '#1a1a1a' }}>
            <CardContent className="p-4">
              <p className="text-xs text-gray-400 uppercase">Logistic Cost</p>
              <p className="text-2xl font-bold mt-1 text-[#BFFF00]">฿{totalLogisticCost.toLocaleString()}</p>
              <div className="flex items-center gap-1 mt-2">
                <Truck size={14} className="text-gray-400" />
                <span className="text-xs text-gray-400">{format(currentMonth, "MMMM yyyy")}</span>
              </div>
            </CardContent>
          </Card>

          {/* Profits Card */}
          <Card className="border-0 shadow-sm rounded-2xl" style={{ backgroundColor: '#1a1a1a' }}>
            <CardContent className="p-4">
              <p className="text-xs text-gray-400 uppercase">Profits</p>
              <p className={`text-2xl font-bold mt-1 ${totalProfits >= 0 ? 'text-[#BFFF00]' : 'text-red-400'}`}>
                ฿{totalProfits.toLocaleString()}
              </p>
              <div className="flex items-center gap-1 mt-2">
                <DollarSign size={14} className="text-gray-400" />
                <span className="text-xs text-gray-400">{format(currentMonth, "MMMM yyyy")}</span>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Event Statistics Cards - Above Calendar */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <Card className="border-0 shadow-sm rounded-2xl" style={{ backgroundColor: '#1a1a1a' }}>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-gray-400 uppercase">Events This Month</p>
                <p className="text-3xl font-bold text-[#BFFF00] mt-1">{eventStats.eventsThisMonth}</p>
                <p className="text-xs text-gray-500 mt-1">{format(new Date(), "MMMM yyyy")}</p>
              </div>
              <div className="w-12 h-12 bg-[#BFFF00]/20 rounded-full flex items-center justify-center">
                <CalendarDays size={24} className="text-[#BFFF00]" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-sm rounded-2xl" style={{ backgroundColor: '#1a1a1a' }}>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-gray-400 uppercase">Events This Year</p>
                <p className="text-3xl font-bold text-[#BFFF00] mt-1">{eventStats.eventsThisYear}</p>
                <p className="text-xs text-gray-500 mt-1">{new Date().getFullYear()}</p>
              </div>
              <div className="w-12 h-12 bg-[#BFFF00]/20 rounded-full flex items-center justify-center">
                <CalendarCheck size={24} className="text-[#BFFF00]" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-sm rounded-2xl" style={{ backgroundColor: '#1a1a1a' }}>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-gray-400 uppercase">Upcoming Events</p>
                <p className="text-3xl font-bold text-[#BFFF00] mt-1">{eventStats.upcomingEvents}</p>
                <p className="text-xs text-gray-500 mt-1">จากวันนี้เป็นต้นไป</p>
              </div>
              <div className="w-12 h-12 bg-[#BFFF00]/20 rounded-full flex items-center justify-center">
                <CalendarClock size={24} className="text-[#BFFF00]" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Large Calendar */}
      <Card className="shadow-lg rounded-2xl" style={{ backgroundColor: '#1a1a1a' }}>
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="text-xl font-semibold text-white">{format(currentMonth, "MMMM yyyy")}</CardTitle>
            <div className="flex items-center gap-1">
              <Button variant="ghost" size="sm" onClick={() => setCurrentMonth(subMonths(currentMonth, 1))} className="h-8 w-8 p-0 hover:bg-white/10 text-gray-400 hover:text-white"><ChevronLeft size={18} /></Button>
              <Button variant="ghost" size="sm" onClick={() => setCurrentMonth(new Date())} className="h-8 px-3 hover:bg-white/10 text-sm text-gray-400 hover:text-white">Today</Button>
              <Button variant="ghost" size="sm" onClick={() => setCurrentMonth(addMonths(currentMonth, 1))} className="h-8 w-8 p-0 hover:bg-white/10 text-gray-400 hover:text-white"><ChevronRight size={18} /></Button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-4">
          {/* Weekday Headers */}
          <div className="grid grid-cols-7 mb-2">
            {["อา", "จ", "อ", "พ", "พฤ", "ศ", "ส"].map((day) => (
              <div key={day} className="text-center text-sm font-medium text-gray-400 py-2">{day}</div>
            ))}
          </div>

          {/* Calendar Grid */}
          <div className="grid grid-cols-7 gap-1">
            {calendarDays.map((day, idx) => {
              const dayEvents = getEventsForDay(day);
              const daySetups = getSetupsForDay(day);
              const dayLeaves = getLeavesForDay(day);
              const isCurrentMonth = isSameMonth(day, currentMonth);
              const isSelected = selectedDate && isSameDay(day, selectedDate);
              const isTodayDate = isToday(day);

              return (
                <div
                  key={idx}
                  onClick={() => setSelectedDate(day)}
                  style={{ backgroundColor: isCurrentMonth ? '#252525' : '#1a1a1a' }}
                  className={`min-h-[100px] p-2 rounded-lg cursor-pointer transition-all border ${isSelected ? "border-[#BFFF00] border-2" : "border-transparent hover:border-gray-600"} ${isTodayDate ? "ring-2 ring-[#BFFF00]/50" : ""}`}
                >
                  <div className={`text-sm font-medium mb-1 w-7 h-7 flex items-center justify-center rounded-full ${isTodayDate ? "bg-[#BFFF00] text-black" : ""} ${isCurrentMonth ? "text-white" : "text-gray-600"}`}>
                    {format(day, "d")}
                  </div>

                  <div className="space-y-1 overflow-hidden">
                    {dayEvents.slice(0, 3).map((event, eventIdx) => {
                      const modelColor = getModelColor(event.model_name);
                      return (
                        <div key={event.id || eventIdx} className="text-[10px] px-1.5 py-0.5 rounded truncate" style={{ backgroundColor: modelColor, color: '#1a1a1a' }} title={`${event.model_name}${event.event_staff ? ' · ' + event.event_staff : ''} - ${event.event_name}`}>
                          <span className="font-semibold">{event.model_name}</span>{event.event_staff ? <span> · {event.event_staff}</span> : null}
                        </div>
                      );
                    })}
                    {dayEvents.length > 3 && <div className="text-[10px] text-gray-400 px-1">+{dayEvents.length - 3} more</div>}

                    {daySetups.slice(0, 2).map((event, si) => (
                      <div key={`setup-${event.id || si}`}
                        className="text-[10px] px-1.5 py-0.5 rounded truncate flex items-center gap-1"
                        style={{ border: '1px dashed #f59e0b', color: '#fcd34d' }}
                        title={`Set-up${event.setup_time ? ' ' + event.setup_time : ''} · ${event.setup_staff || ''} (${event.model_name || event.event_name || ''})`}
                      >
                        <Wrench size={9} style={{ flexShrink: 0 }} />
                        <span className="truncate">Set-up{event.setup_staff ? ' · ' + event.setup_staff : ''}{event.setup_time ? ' ' + event.setup_time : ''}</span>
                      </div>
                    ))}
                    {daySetups.length > 2 && <div className="text-[10px] text-gray-400 px-1">+{daySetups.length - 2} set-up</div>}

                    {dayLeaves.length > 0 && (
                      <div className="mt-0.5 space-y-0.5">
                        {dayLeaves.slice(0, 2).map((leave, li) => {
                          const s = (leave.status === 'pending' ? LEAVE_PENDING_STYLE : LEAVE_STYLE[leave.type]) || LEAVE_PENDING_STYLE;
                          return (
                            <div key={leave.id || li}
                              className="text-[10px] px-1.5 py-0.5 rounded flex items-center gap-1"
                              style={{ backgroundColor: s.bg }}
                              title={`${leave.userName} — ${leave.type}${leave.status === 'pending' ? ' (Pending)' : ''}`}
                            >
                              <span style={{ width: 5, height: 5, borderRadius: '50%', backgroundColor: s.dot, flexShrink: 0, display: 'inline-block' }} />
                              <span style={{ color: s.text, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{leave.userName}</span>
                              <span style={{ fontSize: 9, borderRadius: 3, padding: '0 3px', backgroundColor: s.tagBg, color: s.tagText, flexShrink: 0, fontWeight: 500 }}>{s.label}</span>
                            </div>
                          );
                        })}
                        {dayLeaves.length > 2 && (
                          <div className="text-[10px] text-gray-500 px-1">+{dayLeaves.length - 2} ลา</div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Monthly Events List - Below Calendar */}
      <Card className="shadow-lg rounded-2xl mt-6" style={{ backgroundColor: '#1a1a1a' }}>
        <CardHeader className="pb-2 border-b border-gray-800">
          <CardTitle className="text-lg font-semibold text-white flex items-center gap-2">
            <CalendarIcon size={18} className="text-[#BFFF00]" />
            Events ใน {format(currentMonth, "MMMM yyyy")}
            <span className="ml-2 px-2 py-0.5 bg-[#BFFF00]/20 text-[#BFFF00] text-sm rounded-full">
              {events.filter(e => {
                const d = e.date_start || e.date;
                if (!d) return false;
                try {
                  const date = parseISO(d);
                  return isSameMonth(date, currentMonth);
                } catch { return false; }
              }).length} events
            </span>
          </CardTitle>
        </CardHeader>
        <CardContent className="p-4">
          {(() => {
            const monthEvents = events
              .filter(e => {
                const d = e.date_start || e.date;
                if (!d) return false;
                try {
                  const date = parseISO(d);
                  return isSameMonth(date, currentMonth);
                } catch { return false; }
              })
              .sort((a, b) => {
                const dateA = a.date_start || a.date;
                const dateB = b.date_start || b.date;
                return compareAsc(parseISO(dateA), parseISO(dateB));
              });

            if (monthEvents.length === 0) {
              return (
                <div className="text-center py-8 text-gray-500">
                  ไม่มี Event ในเดือนนี้
                </div>
              );
            }

            return (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {monthEvents.map((event) => (
                  <div
                    key={event.id}
                    className="p-4 rounded-xl border border-gray-700 hover:border-[#BFFF00]/50 transition-all cursor-pointer"
                    style={{ backgroundColor: '#252525' }}
                    onClick={() => handleEdit(event)}
                  >
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <div 
                          className="w-3 h-10 rounded-full" 
                          style={{ backgroundColor: getModelColor(event.model_name) }}
                        />
                        <div>
                          <p className="text-sm font-semibold text-white">{event.model_name || '-'}</p>
                          <p className="text-xs text-gray-400">{event.event_name || '-'}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-1">
                        <span className={`px-2 py-0.5 text-xs rounded-full ${
                          event.status === 'completed' ? 'bg-green-500/20 text-green-400' :
                          event.status === 'confirmed' ? 'bg-blue-500/20 text-blue-400' :
                          event.status === 'cancelled' ? 'bg-red-500/20 text-red-400' :
                          'bg-yellow-500/20 text-yellow-400'
                        }`}>
                          {event.status || 'scheduled'}
                        </span>
                        <button
                          onClick={(e) => { e.stopPropagation(); handleDelete(event.id); }}
                          className="p-1 rounded hover:bg-red-500/20 text-gray-500 hover:text-red-400 transition-colors"
                        >
                          <Trash2 size={13} />
                        </button>
                      </div>
                    </div>
                    
                    <div className="space-y-2 text-xs">
                      <div className="flex items-center gap-2 text-gray-400">
                        <CalendarDays size={12} className="text-[#BFFF00]" />
                        <span>
                          {event.date_start ? format(parseISO(event.date_start), "d MMM yyyy") : '-'}
                          {event.date_end && event.date_end !== event.date_start && (
                            <span> - {format(parseISO(event.date_end), "d MMM yyyy")}</span>
                          )}
                        </span>
                      </div>
                      
                      {event.time && (
                        <div className="flex items-center gap-2 text-gray-400">
                          <Clock size={12} className="text-[#BFFF00]" />
                          <span>{event.time}{event.time_end && ` - ${event.time_end}`}</span>
                        </div>
                      )}
                      
                      {event.location && (
                        <div className="flex items-center gap-2 text-gray-400">
                          <MapPin size={12} className="text-[#BFFF00]" />
                          <span className="truncate">{event.location}</span>
                        </div>
                      )}
                      
                      <div className="flex items-center justify-between pt-2 border-t border-gray-700 mt-2">
                        <span className="text-[#BFFF00] font-semibold">
                          ฿{(parseFloat(event.price) || 0).toLocaleString()}
                        </span>
                        <span className={`text-xs ${
                          event.full_paid ? 'text-green-400' :
                          event.deposit_status === 'paid' ? 'text-emerald-400' :
                          event.deposit_status === 'partial' ? 'text-yellow-400' :
                          'text-gray-500'
                        }`}>
                          {event.full_paid ? 'Paid' :
                           event.deposit_status === 'paid' ? 'มัดจำแล้ว' :
                           event.deposit_status === 'partial' ? 'Partial' :
                           event.deposit_status === 'not_required' ? '-' : 'Pending'}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            );
          })()}
        </CardContent>
      </Card>

      {/* Activity Feed */}
      {isAdmin() && activityLogs.length > 0 && (
        <Card className="shadow-lg rounded-2xl mt-6" style={{ backgroundColor: '#1a1a1a' }}>
          <CardHeader className="pb-2 border-b border-gray-800">
            <CardTitle className="text-lg font-semibold text-white flex items-center gap-2">
              <Activity size={16} className="text-gray-400"/> Activity Log
              <span className="ml-auto text-xs text-gray-500 font-normal">30 รายการล่าสุด</span>
            </CardTitle>
          </CardHeader>
          <CardContent className="p-4">
            <div className="space-y-1">
              {activityLogs.map((log) => {
                const meta = ACTION_META[log.action] || { color: '#6b7280' };
                const ts = log.createdAt?.toDate ? log.createdAt.toDate() : null;
                const timeStr = ts ? `${String(ts.getHours()).padStart(2,'0')}:${String(ts.getMinutes()).padStart(2,'0')}` : '';
                const dateStr = ts ? `${ts.getDate()}/${ts.getMonth()+1}` : '';
                return (
                  <div key={log.id} className="flex items-start gap-3 py-2 border-b border-gray-800/60 last:border-0">
                    <span className="flex-shrink-0 w-5 flex justify-center pt-0.5">
                      <span style={{ width: 8, height: 8, borderRadius: '50%', background: meta.color, display: 'inline-block', marginTop: 2 }}/>
                    </span>
                    <div className="flex-1 min-w-0">
                      <span className="text-[12px] font-medium" style={{ color: '#e5e7eb' }}>{log.userName}</span>
                      <span className="text-[11px] text-gray-500 mx-1.5">—</span>
                      <span className="text-[11px]" style={{ color: meta.color }}>{meta.label}</span>
                      {log.detail && <span className="text-[11px] text-gray-500 ml-1.5 truncate">{log.detail}</span>}
                    </div>
                    <div className="text-right flex-shrink-0">
                      <div className="text-[11px] text-gray-400">{timeStr}</div>
                      <div className="text-[10px] text-gray-600">{dateStr}</div>
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Add/Edit Event Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-white border-gray-200 max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-gray-900">{editingEvent ? "แก้ไข Event" : "เพิ่ม Event ใหม่"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4 mt-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-gray-700">ชื่อ Event</Label>
                <Input value={formData.event_name} onChange={(e) => setFormData({ ...formData, event_name: e.target.value })} placeholder="ชื่อ Event" required className="border-gray-200" />
              </div>
              <div className="space-y-2">
                <Label className="text-gray-700">Model</Label>
                <Select value={formData.model_name} onValueChange={(v) => setFormData({ ...formData, model_name: v })}>
                  <SelectTrigger className="border-gray-200">
                    {formData.model_name ? (
                      <div className="flex items-center gap-2">
                        <span className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: getModelColor(formData.model_name) }} />
                        <span>{formData.model_name}</span>
                      </div>
                    ) : (
                      <SelectValue placeholder="เลือก Model" />
                    )}
                  </SelectTrigger>
                  <SelectContent className="bg-white border-gray-200">
                    {models
                      .filter((m) => m.available_for_event !== false || m.model_name === formData.model_name)
                      .map((m) => (
                      <SelectItem key={m.id} value={m.model_name}>
                        <div className="flex items-center gap-2">
                          <span className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: m.tag_color || "#BFFF00" }} />
                          <span>{m.model_name}</span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-gray-700">สถานที่</Label>
                <Input value={formData.location} onChange={(e) => setFormData({ ...formData, location: e.target.value })} placeholder="สถานที่จัดงาน" className="border-gray-200" />
              </div>
              <div className="space-y-2">
                <Label className="text-gray-700 flex items-center gap-1"><MapPin size={14} className="text-red-500" />Google Maps Link</Label>
                <Input type="url" value={formData.location_map_link} onChange={(e) => setFormData({ ...formData, location_map_link: e.target.value })} placeholder="https://maps.google.com/..." className="border-gray-200" />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-gray-700">วันเริ่ม</Label>
                <Input type="date" value={formData.date_start} onChange={(e) => setFormData({ ...formData, date_start: e.target.value })} required className="border-gray-200" />
              </div>
              <div className="space-y-2">
                <Label className="text-gray-700">วันสิ้นสุด</Label>
                <Input type="date" value={formData.date_end} onChange={(e) => setFormData({ ...formData, date_end: e.target.value })} className="border-gray-200" />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-gray-700">เวลาเริ่ม</Label>
                <Input type="time" value={formData.time} onChange={(e) => setFormData({ ...formData, time: e.target.value })} className="border-gray-200" />
              </div>
              <div className="space-y-2">
                <Label className="text-gray-700">เวลาสิ้นสุด</Label>
                <Input type="time" value={formData.time_end} onChange={(e) => setFormData({ ...formData, time_end: e.target.value })} className="border-gray-200" />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-gray-700">ราคา (฿)</Label>
                <Input type="number" value={formData.price} onChange={(e) => setFormData({ ...formData, price: parseFloat(e.target.value) || 0 })} className="border-gray-200" />
              </div>
              <div className="space-y-2">
                <Label className="text-gray-700">ค่าขนส่ง (฿)</Label>
                <Input type="number" value={formData.transportation_price} onChange={(e) => setFormData({ ...formData, transportation_price: parseFloat(e.target.value) || 0 })} className="border-gray-200" />
              </div>
            </div>

            {/* Staff — free text with de-duplicated suggestions from the register */}
            <datalist id="event-staff-names">
              {[...new Set(staff.map(s => (s.name || "").trim()).filter(Boolean))]
                .sort((a, b) => a.localeCompare(b, "th"))
                .map((name) => (<option key={name} value={name} />))}
            </datalist>
            <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
              <div className="space-y-2 sm:col-span-2">
                <Label className="text-gray-700">Set-up Staff</Label>
                <Input list="event-staff-names" value={formData.setup_staff}
                  onChange={(e) => setFormData({ ...formData, setup_staff: e.target.value })}
                  placeholder="พิมพ์ชื่อ หรือเลือกจากรายชื่อ" className="border-gray-200" />
              </div>
              <div className="space-y-2">
                <Label className="text-gray-700">วันที่ Set-up</Label>
                <Input type="date" value={formData.setup_date}
                  onChange={(e) => setFormData({ ...formData, setup_date: e.target.value })}
                  className="border-gray-200" />
              </div>
              <div className="space-y-2">
                <Label className="text-gray-700">เวลา Set-up</Label>
                <Input type="time" value={formData.setup_time}
                  onChange={(e) => setFormData({ ...formData, setup_time: e.target.value })}
                  className="border-gray-200" />
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-gray-700">Event Staff</Label>
                <Input list="event-staff-names" value={formData.event_staff}
                  onChange={(e) => setFormData({ ...formData, event_staff: e.target.value })}
                  placeholder="พิมพ์ชื่อ หรือเลือกจากรายชื่อ" className="border-gray-200" />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-gray-700">สถานะมัดจำ</Label>
                <Select value={formData.deposit_status} onValueChange={(v) => setFormData({ ...formData, deposit_status: v })}>
                  <SelectTrigger className="border-gray-200"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white border-gray-200">
                    {depositStatuses.map((s) => (<SelectItem key={s} value={s}>{s}</SelectItem>))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-gray-700">สถานะ Event</Label>
                <Select value={formData.status} onValueChange={(v) => setFormData({ ...formData, status: v })}>
                  <SelectTrigger className="border-gray-200"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white border-gray-200">
                    {statuses.map((s) => (<SelectItem key={s} value={s}>{s}</SelectItem>))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center gap-2 pt-6">
                <Checkbox checked={formData.full_paid} onCheckedChange={(c) => setFormData({ ...formData, full_paid: c })} />
                <Label className="text-gray-700">ชำระเต็มจำนวนแล้ว</Label>
              </div>
            </div>

            {/* Event Display & Template */}
            <div className="p-4 bg-blue-50 rounded-lg space-y-3">
              <Label className="text-gray-700 font-medium flex items-center gap-2"><FileImage size={16} />Event Display</Label>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Input type="url" value={formData.event_display_link} onChange={(e) => setFormData({ ...formData, event_display_link: e.target.value })} placeholder="Link..." className="border-gray-200 bg-white" />
                <Select value={formData.event_display_status} onValueChange={(v) => setFormData({ ...formData, event_display_status: v })}>
                  <SelectTrigger className="border-gray-200 bg-white"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white">{uploadStatuses.map((s) => (<SelectItem key={s} value={s}>{s}</SelectItem>))}</SelectContent>
                </Select>
              </div>
            </div>

            <div className="p-4 bg-purple-50 rounded-lg space-y-3">
              <Label className="text-gray-700 font-medium flex items-center gap-2"><FileText size={16} />Template</Label>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Input type="url" value={formData.template_link} onChange={(e) => setFormData({ ...formData, template_link: e.target.value })} placeholder="Link..." className="border-gray-200 bg-white" />
                <Select value={formData.template_status} onValueChange={(v) => setFormData({ ...formData, template_status: v })}>
                  <SelectTrigger className="border-gray-200 bg-white"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white">{uploadStatuses.map((s) => (<SelectItem key={s} value={s}>{s}</SelectItem>))}</SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-gray-700 flex items-center gap-1"><StickyNote size={14} className="text-amber-500" />Note</Label>
              <Textarea value={formData.note} onChange={(e) => setFormData({ ...formData, note: e.target.value })} placeholder="หมายเหตุ..." className="border-gray-200 min-h-[80px]" />
            </div>

            <div className="flex gap-3 pt-4">
              <Button type="button" variant="outline" onClick={resetForm} className="flex-1 border-gray-200">ยกเลิก</Button>
              <Button type="submit" className="flex-1 bg-[#BFFF00] hover:bg-[#a6e600] text-black">{editingEvent ? "อัพเดท" : "สร้าง Event"}</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Dashboard;
